import React, { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import { IconButton, MenuItem, Select, Tooltip, Menu, Box, Typography, Divider, Button } from "@mui/material";

const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
  const day = date.getDate().toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const defaultLabels = {
  start: {
    label: "Created",
    value: "created"
  },
  end: {
    label: "Modified",
    value: "modified"
  }
}
const CalendarRangePicker = ({ onChange, filterType="modified", labels=defaultLabels, value=[] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  // Initialize showPicker based on whether we have a saved value
  const [showPicker, setShowPicker] = useState(value && value.length >= 2 && value[0] && value[1]);

  // Initialize dates from saved value if available
  const [startDate, setStartDate] = useState(value && value[0] ? new Date(value[0]) : null);
  const [endDate, setEndDate] = useState(value && value[1] ? new Date(value[1]) : null);
  const [dateFilterType, setDateFilterType] = useState(value && value[2] ? value[2] : filterType);

  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);

  // Initialize selectedRange from saved value
  const [selectedRange, setSelectedRange] = useState(
    value && value.length >= 2 && value[0] && value[1] 
      ? [new Date(value[0]), new Date(value[1]), value[2] || filterType] 
      : []
  );
  
  // Track if this is the initial mount to prevent clearing saved values
  const [isInitialMount, setIsInitialMount] = useState(true);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setIsStartDateOpen(false);
    if (!endDate) setIsEndDateOpen(true);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setIsEndDateOpen(false);
  };

  const handleApply = () => {
    let newStartDate = startDate || selectedRange[0];
    let newEndDate = endDate || selectedRange[1];
    let newDateFilterType = selectedRange[2] || dateFilterType;

    setSelectedRange([newStartDate, newEndDate, newDateFilterType]);
  };

  const handleClear = () => {
    setSelectedRange([]);
    setStartDate(null);
    setEndDate(null);
    setShowPicker(false);
    setAnchorEl(null);
  };

  // Sync internal state when parent value is cleared externally (e.g., from chip X button)
  useEffect(() => {
    if (!value || value.length === 0 || !value[0] || !value[1]) {
      // Parent cleared the value, reset internal state
      if (showPicker || selectedRange.length > 0) {
        setSelectedRange([]);
        setStartDate(null);
        setEndDate(null);
        setShowPicker(false);
      }
    }
  }, [value]);

  useEffect(() => {
    if (startDate && endDate && dateFilterType) {
      handleApply();
      setShowPicker(true);
    }
  }, [startDate, endDate, dateFilterType]);

  useEffect(() => {
    // Skip the initial mount effect if we have saved values to prevent clearing them
    if (isInitialMount) {
      setIsInitialMount(false);
      // If we have initial values, don't trigger onChange as they're already set
      if (value && value.length >= 2 && value[0] && value[1]) {
        return;
      }
    }
    
    if (!selectedRange[0] || !selectedRange[1]) {
      onChange([]);
    } else {
      if (selectedRange[0] && selectedRange[1]) {
        const newSelectedRange = [
          formatDateToYYYYMMDD(new Date(selectedRange[0])),
          formatDateToYYYYMMDD(new Date(selectedRange[1])),
          dateFilterType,
        ];
        onChange(newSelectedRange);
      }
    }
  }, [selectedRange, dateFilterType]);

  return (
    <>
      <Tooltip title={showPicker ? "Edit Date Filter" : "Filter By Date"}>
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          size="small"
          sx={{
            color: showPicker ? '#1976d2' : 'inherit',
            bgcolor: showPicker ? '#e3f2fd' : 'transparent'
          }}
        >
          <EventIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: { minWidth: 400, p: 2 }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Date Filter
          </Typography>
          <Divider />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Filter Type
          </Typography>
          <Select
            fullWidth
            size="small"
            value={dateFilterType}
            onChange={(e) => {
              setDateFilterType(e.target.value);
            }}
          >
            {Object.entries(labels).map(([key, { label, value }]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              Start Date
            </Typography>
            <DatePicker
              maxDate={endDate ? endDate : null}
              open={isStartDateOpen}
              onAccept={handleStartDateChange}
              value={startDate || selectedRange[0] || null}
              onOpen={() => {
                setIsStartDateOpen(true);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              End Date
            </Typography>
            <DatePicker
              minDate={startDate ? startDate : null}
              open={isEndDateOpen}
              onAccept={handleEndDateChange}
              value={endDate || selectedRange[1] || null}
              onOpen={() => {
                if (selectedRange[0] && !startDate) {
                  setStartDate(selectedRange[0]);
                }
                if (!startDate) {
                  setIsEndDateOpen(false);
                  setIsStartDateOpen(true);
                } else {
                  setIsStartDateOpen(false);
                  setIsEndDateOpen(true);
                }
              }}
              onClose={() => {
                setIsEndDateOpen(false);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </Box>
        </LocalizationProvider>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={handleClear}
            variant="outlined"
          >
            Clear
          </Button>
          <Button
            size="small"
            onClick={() => setAnchorEl(null)}
            variant="contained"
            disabled={!startDate || !endDate}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default CalendarRangePicker;
